import type { BrowserSessionResource } from '@onefootprint/request-types/percy';
import type { ChatMessage, ResourceMessage } from '../hooks/use-chat-ws';
import {
  getParentToolUseId,
  getSpawnSubagentType,
  getTaskStatus,
  isSubagentSpawn,
  isTerminalTaskStatus,
} from './journey-attribution';
import { isGetAgentRunTool, OPEN_IN_DASHBOARD_TOOL_NAME, RESOURCE_CREATION_TOOL_OUTPUT_KIND } from './tool-names';

// The Segment model + the pure predicates/formatters that classify the chat stream into
// messages, tool groups, resource-card groups, and journey progress cards.
export type Segment =
  | { kind: 'message'; id: string; message: ChatMessage }
  | { kind: 'tool_group'; id: string; messages: ChatMessage[] }
  | { kind: 'resource_group'; id: string; groupKey: string; messages: ChatMessage[] }
  | { kind: 'journey_group'; id: string; spawnId: string; agent: string; spawn: ChatMessage; messages: ChatMessage[] }
  // ≥2 consecutive journey_group spawns coalesced into one feed container (see coalesceJourneyBatches).
  | { kind: 'journey_batch'; id: string; groups: JourneyGroupSegment[] }
  | { kind: 'browser_run'; id: string; messages: ChatMessage[]; open: boolean; endedAt?: string };

export type JourneyGroupSegment = Extract<Segment, { kind: 'journey_group' }>;
export type JourneyBatchSegment = Extract<Segment, { kind: 'journey_batch' }>;
export type BrowserRunSegment = Extract<Segment, { kind: 'browser_run' }>;

// Coalesce runs of ≥2 consecutive journey_group segments into one journey_batch, so a turn that
// spawns several sub-agents reads as one container in the feed instead of a stack of cards. A lone
// spawn stays a journey_group (single reference).
export const coalesceJourneyBatches = (segments: Segment[]): Segment[] => {
  const out: Segment[] = [];
  let run: JourneyGroupSegment[] = [];
  const flush = () => {
    if (run.length === 1) out.push(run[0]);
    else if (run.length > 1) out.push({ kind: 'journey_batch', id: `jb-${run[0].id}`, groups: run });
    run = [];
  };
  for (const seg of segments) {
    if (seg.kind === 'journey_group') run.push(seg);
    else {
      flush();
      out.push(seg);
    }
  }
  flush();
  return out;
};

export type RenderableMessage = { msg: ChatMessage; originalIndex: number };

export const isPollToolMessage = (msg: ChatMessage): boolean => {
  return msg.type === 'tool_use' && isGetAgentRunTool(msg.tool);
};

export const isPollOnlyToolGroup = (seg: Segment | undefined): seg is Extract<Segment, { kind: 'tool_group' }> => {
  return (
    seg?.kind === 'tool_group' &&
    seg.messages.some(isPollToolMessage) &&
    seg.messages.every(m => m.type !== 'tool_use' || isPollToolMessage(m))
  );
};

// Created-resource kinds that join into a block. Any consecutive run of them — regardless of kind
// — folds into one block (a lone one still renders as a standalone card), so a whole burst of
// creations reads as a single unit. `ui_block` is excluded (it renders its own UI); `agent_suggestion`
// keeps its own per-agent group so each batch collapses into one aggregate card.
const COLLAPSIBLE_RESOURCE_KINDS = new Set(['onboarding', 'entity', 'investigation', 'agent', 'playbook', 'artifact']);
const RESOURCE_CREATED_GROUP_KEY = 'resource:created';

// Per-suggestion events group per agent so the panel collapses each batch into one card.
export const AGENT_SUGGESTION_GROUP_PREFIX = 'agent_suggestion:';

const isBrowserSessionFrame = (msg: ChatMessage): msg is ResourceMessage & { resource: BrowserSessionResource } =>
  msg.type === 'resource' && msg.resource?.kind === 'browser_session';

export type BrowserAsk =
  | { kind: 'handoff'; id: string; reason: string; message: string; acceptsCode: boolean }
  | { kind: 'credential'; id: string; origin: string; reason: string };

const HANDOFF_TOOL = 'mcp__browser__wait_for_user';
const REQUEST_CREDENTIAL_TOOL = 'mcp__credentials__request_credential';

export const activeBrowserAsk = (messages: ChatMessage[]): BrowserAsk | null => {
  let ask: BrowserAsk | null = null;
  for (const msg of messages) {
    if (msg.type === 'user') ask = null;
    if (msg.type === 'approval_required' && msg.kind === 'browser_takeover') {
      ask = {
        kind: 'handoff',
        id: msg.approval_id,
        reason: msg.header ?? '',
        message: msg.description ?? '',
        acceptsCode: msg.accepts_code === true,
      };
    }
    if (msg.type !== 'tool_use' || getParentToolUseId(msg)) continue;
    if (msg.tool === REQUEST_CREDENTIAL_TOOL) {
      ask = {
        kind: 'credential',
        id: msg.id,
        origin: String(msg.input.origin ?? ''),
        reason: String(msg.input.reason ?? ''),
      };
    } else if (msg.tool !== HANDOFF_TOOL) {
      ask = null;
    }
  }
  return ask;
};

export const liveBrowserSession = (messages: ChatMessage[]): BrowserSessionResource | null => {
  const latest = messages.filter(isBrowserSessionFrame).at(-1)?.resource ?? null;
  return latest?.status === 'live' ? latest : null;
};

export const hiddenBrowserSessions = (messages: ChatMessage[]): Set<ChatMessage> => {
  const frames = messages.filter(isBrowserSessionFrame);
  const ended = frames.filter(
    frame => frame.resource.status === 'ended' && frame.resource.current_url.startsWith('http'),
  );
  const lastEnded = new Map(ended.map(frame => [frame.resource.id, frame]));
  return new Set(frames.filter(frame => lastEnded.get(frame.resource.id) !== frame));
};

export const getResourceCreateGroupKey = (msg: ChatMessage): string | null => {
  if (msg.type !== 'resource' || msg.kind !== 'create' || !msg.resource) return null;
  if (msg.resource.kind === 'agent_suggestion') return `${AGENT_SUGGESTION_GROUP_PREFIX}${msg.resource.agent_id}`;
  if (!COLLAPSIBLE_RESOURCE_KINDS.has(msg.resource.kind)) return null;
  return RESOURCE_CREATED_GROUP_KEY;
};

export const isAgentSuggestionGroupKey = (groupKey: string): boolean => {
  return groupKey.startsWith(AGENT_SUGGESTION_GROUP_PREFIX);
};

const plural = (count: number, singular: string, pluralForm = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

// The label group a created resource summarizes under: entities split by kind, and an agent-run
// investigation (one with an agent_execution_id) reads as an "agent run" apart from plain ones.
const resourceLabelKind = (resource: NonNullable<ResourceMessage['resource']>): string => {
  if (resource.kind === 'entity') return resource.entity_kind;
  if (resource.kind === 'investigation') return resource.agent_execution_id ? 'agent_run' : 'investigation';
  return resource.kind;
};

const RESOURCE_LABEL: Record<string, (n: number) => string> = {
  person: n => `Created ${plural(n, 'person', 'people')}`,
  business: n => `Created ${plural(n, 'business', 'businesses')}`,
  onboarding: n => `Started ${plural(n, 'onboarding')}`,
  agent_run: n => `Started ${plural(n, 'agent run')}`,
  investigation: n => `Created ${plural(n, 'investigation')}`,
  agent: n => `Created ${plural(n, 'agent')}`,
  playbook: n => `Created ${plural(n, 'playbook draft')}`,
  artifact: n => `Created ${plural(n, 'document')}`,
};

// The header summary for a joined resource block: the kind-specific count when the whole run is a
// single kind, else a generic resource count (the run mixes kinds).
export const summarizeResourceCreatedGroup = (resources: ResourceMessage[]): string => {
  const kinds = new Set(resources.map(r => (r.resource ? resourceLabelKind(r.resource) : 'resource')));
  const only = kinds.size === 1 ? [...kinds][0] : null;
  if (only && RESOURCE_LABEL[only]) return RESOURCE_LABEL[only](resources.length);
  return `Created ${plural(resources.length, 'resource')}`;
};

// `count` is the batch size, not `summaries.length`, so a legacy row with a null summary is still
// counted (matches the panel's pending total). Summaries drop the empties for the subtitle.
export const agentSuggestionsFromGroup = (
  messages: ChatMessage[],
): { agentId: string; count: number; summaries: string[] } => {
  const summaries: string[] = [];
  let agentId = '';
  let count = 0;
  for (const m of messages) {
    if (m.type === 'resource' && m.kind === 'create' && m.resource?.kind === 'agent_suggestion') {
      agentId = m.resource.agent_id;
      count += 1;
      if (m.resource.summary) summaries.push(m.resource.summary);
    }
  }
  return { agentId, count, summaries };
};

export const isResourceCreateMessage = (msg: ChatMessage): msg is ResourceMessage => {
  return getResourceCreateGroupKey(msg) !== null;
};

// The resource-creation (and open_in_dashboard) tool_use ids whose card has landed — their
// (redundant) rows are hidden. Each call is paired, in order, with a later create card of the kind it
// produces, or an open call with the next open marker; an unpaired call (a failure, or still
// in-flight — no card yet) keeps its row visible.
export const resourceCreationToolsWithCard = (messages: ChatMessage[]): Set<string> => {
  const pendingByKind = new Map<string, string[]>();
  const withCard = new Set<string>();
  for (const msg of messages) {
    if (msg.type === 'tool_use') {
      const kind = msg.tool === OPEN_IN_DASHBOARD_TOOL_NAME ? 'open' : RESOURCE_CREATION_TOOL_OUTPUT_KIND[msg.tool];
      if (kind) {
        const queue = pendingByKind.get(kind);
        if (queue) queue.push(msg.id);
        else pendingByKind.set(kind, [msg.id]);
      }
    } else if (msg.type === 'resource' && (msg.kind === 'create' || msg.kind === 'open') && msg.resource) {
      const toolId = pendingByKind.get(msg.kind === 'open' ? 'open' : msg.resource.kind)?.shift();
      if (toolId) withCard.add(toolId);
    }
  }
  return withCard;
};

// Each `suggest_agent_edits` round re-emits the full pending set as a contiguous batch, so an
// iterating turn stacks several batches per agent. They point at the Changes panel, not a log —
// keep only each agent's latest batch so the chat shows one card whose count mirrors the panel.
export const supersededAgentSuggestions = (messages: ChatMessage[]): Set<ChatMessage> => {
  const agentIdOf = (m: ChatMessage): string | null =>
    m.type === 'resource' && m.kind === 'create' && m.resource?.kind === 'agent_suggestion'
      ? m.resource.agent_id
      : null;
  const runs: { agentId: string; msgs: ChatMessage[] }[] = [];
  let current: { agentId: string; msgs: ChatMessage[] } | null = null;
  for (const msg of messages) {
    const agentId = agentIdOf(msg);
    if (agentId === null) {
      current = null;
      continue;
    }
    if (current && current.agentId === agentId) {
      current.msgs.push(msg);
    } else {
      current = { agentId, msgs: [msg] };
      runs.push(current);
    }
  }
  const latestRunIdxByAgent = new Map<string, number>();
  runs.forEach((run, i) => {
    latestRunIdxByAgent.set(run.agentId, i);
  });
  const hidden = new Set<ChatMessage>();
  runs.forEach((run, i) => {
    if (latestRunIdxByAgent.get(run.agentId) !== i) {
      for (const msg of run.msgs) hidden.add(msg);
    }
  });
  return hidden;
};

// Journey buckets self-dedup: the `ready` history replay is the raw wire log (it
// bypasses processMessage), so the SDK's repeated partial frames are present verbatim.
const appendToJourneyGroup = (group: JourneyGroupSegment, msg: ChatMessage): void => {
  if (msg.type === 'tool_use') {
    const idx = group.messages.findLastIndex(m => m.type === 'tool_use' && m.id === msg.id);
    if (idx >= 0) {
      group.messages[idx] = msg;
      return;
    }
  }
  if ((msg.type === 'thinking' || msg.type === 'text') && msg.started_at) {
    const idx = group.messages.findLastIndex(
      m => m.type === msg.type && (m.type === 'thinking' || m.type === 'text') && m.started_at === msg.started_at,
    );
    if (idx >= 0) {
      group.messages[idx] = msg;
      return;
    }
  }
  if (msg.type === 'task_progress') {
    const idx = group.messages.findLastIndex(m => m.type === 'task_progress' && m.data.task_id === msg.data.task_id);
    if (idx >= 0) {
      const existing = group.messages[idx];
      // A late heartbeat must not flip a terminal lifecycle row back to running.
      if (isTerminalTaskStatus(getTaskStatus(existing)) && !isTerminalTaskStatus(getTaskStatus(msg))) return;
      group.messages[idx] = msg;
      return;
    }
  }
  group.messages.push(msg);
};

/**
 * Classify the renderable stream into segments. Delegated-subagent frames
 * (attributed via `parent_tool_use_id`) bucket into a `journey_group` anchored at
 * their spawn's position, no matter how far they interleave with later turns —
 * background subagents keep emitting after the orchestrator's turn completes. This
 * covers every delegated Task/Agent, not just the "journey" subagents: without it
 * a generic delegate's thinking/tools/text stream inline at the orchestrator's own
 * depth, which reads as two voices at one level. A spawn only becomes a card when
 * at least one attributed frame exists for it, so sessions recorded before
 * attribution existed render exactly as they always did.
 */
export const buildSegments = (renderable: RenderableMessage[], live = false): Segment[] => {
  const attributedIds = new Set<string>();
  for (const { msg } of renderable) {
    const pid = getParentToolUseId(msg);
    if (pid) attributedIds.add(pid);
  }

  const journeyGroups = new Map<string, JourneyGroupSegment>();
  const result: Segment[] = [];
  let openRun: BrowserRunSegment | null = null;
  for (const { msg, originalIndex } of renderable) {
    const pid = getParentToolUseId(msg);
    if (pid) {
      const group = journeyGroups.get(pid);
      if (group) {
        appendToJourneyGroup(group, msg);
        continue;
      }
      // Attributed frame whose spawn frame didn't make it into the stream — fall
      // through to the inline flow (nothing to anchor a card to).
    }
    if (msg.type === 'tool_use' && isSubagentSpawn(msg) && attributedIds.has(msg.id)) {
      const group: JourneyGroupSegment = {
        kind: 'journey_group',
        id: `jg-${msg.id}`,
        spawnId: msg.id,
        agent: getSpawnSubagentType(msg),
        spawn: msg,
        messages: [],
      };
      journeyGroups.set(msg.id, group);
      result.push(group);
      continue;
    }

    if (openRun && isBrowserRunMessage(msg)) {
      openRun.messages.push(msg);
      continue;
    }
    if (!openRun && isBrowserToolMessage(msg)) {
      openRun = { kind: 'browser_run', id: `br-${msg.id}`, messages: [msg], open: false };
      result.push(openRun);
      continue;
    }
    if (openRun && isBrowserRunBypass(msg)) {
      appendInlineSegment(result, msg, originalIndex);
      continue;
    }
    openRun = null;
    appendInlineSegment(result, msg, originalIndex);
  }
  if (openRun && live) openRun.open = true;
  return popBrowserRunAnswers(result);
};

const BROWSER_TOOL_PREFIXES = ['mcp__browser__', 'mcp__credentials__'];
const HANDS_OFF_TOOLS = ['mcp__browser__wait_for_user', 'mcp__credentials__request_credential'];

export const isAgentBrowserCommand = (input: Record<string, unknown>): boolean =>
  String(input.command ?? '').includes('agent-browser');

const isHandsOff = (msg: ChatMessage): boolean => msg.type === 'tool_use' && HANDS_OFF_TOOLS.includes(msg.tool);

const isBrowserToolMessage = (msg: ChatMessage): msg is Extract<ChatMessage, { type: 'tool_use' }> =>
  msg.type === 'tool_use' &&
  !isHandsOff(msg) &&
  (BROWSER_TOOL_PREFIXES.some(prefix => msg.tool.startsWith(prefix)) ||
    (msg.tool === 'mcp__workspace__bash' && isAgentBrowserCommand(msg.input)));

const isBrowserRunMessage = (msg: ChatMessage): boolean =>
  (isGroupableTool(msg) && !isHandsOff(msg)) || msg.type === 'thinking' || msg.type === 'text';

const isBrowserRunBypass = (msg: ChatMessage): boolean =>
  msg.type === 'resource' || (msg.type === 'tool_use' && msg.tool === 'TodoWrite');

const popBrowserRunAnswers = (segments: Segment[]): Segment[] =>
  segments.flatMap((seg): Segment[] => {
    if (seg.kind !== 'browser_run' || seg.open) return [seg];
    let cut = seg.messages.length;
    while (cut > 1 && seg.messages[cut - 1].type === 'text') cut -= 1;
    if (cut === seg.messages.length) return [seg];
    const answers = seg.messages.slice(cut);
    return [
      { ...seg, messages: seg.messages.slice(0, cut), endedAt: answers[0].timestamp },
      ...answers.map((message, i): Segment => ({ kind: 'message', id: `${seg.id}-answer-${i}`, message })),
    ];
  });

const isGroupableTool = (msg: ChatMessage): boolean =>
  (msg.type === 'tool_use' && msg.tool !== 'AskUserQuestion' && msg.tool !== 'TodoWrite') ||
  msg.type === 'task_progress';

// A segment that owns a `messages` array — the only kinds a new message can be folded into.
type GroupSegment = Extract<Segment, { messages: ChatMessage[] }>;

// Whether `msg` belongs in the currently-open segment rather than starting a new one. Narrows
// `last` to a `GroupSegment` when true, so the caller can push into `last.messages`.
const extendsOpenSegment = (last: Segment | undefined, msg: ChatMessage): last is GroupSegment => {
  // An inter-item `thinking` folds into an open poll burst or resource batch so it doesn't
  // split it into N lines.
  if (msg.type === 'thinking') return isPollOnlyToolGroup(last) || last?.kind === 'resource_group';
  const resourceGroupKey = getResourceCreateGroupKey(msg);
  if (resourceGroupKey) return last?.kind === 'resource_group' && last.groupKey === resourceGroupKey;
  // Poll bursts stay in their own group — never merge polls with other tools.
  if (isGroupableTool(msg)) return last?.kind === 'tool_group' && isPollOnlyToolGroup(last) === isPollToolMessage(msg);
  return false;
};

const newInlineSegment = (msg: ChatMessage, originalIndex: number): Segment => {
  const resourceGroupKey = getResourceCreateGroupKey(msg);
  if (resourceGroupKey)
    return { kind: 'resource_group', id: `rg-${originalIndex}`, groupKey: resourceGroupKey, messages: [msg] };
  if (isGroupableTool(msg)) {
    const id = msg.type === 'tool_use' ? `tg-${msg.id}` : `tg-tp-${originalIndex}`;
    return { kind: 'tool_group', id, messages: [msg] };
  }
  return { kind: 'message', id: `msg-${originalIndex}`, message: msg };
};

// Non-journey classification: extend the open message/tool/resource group when the message
// belongs to it, else start a new segment.
const appendInlineSegment = (result: Segment[], msg: ChatMessage, originalIndex: number): void => {
  const last = result[result.length - 1];
  if (last && extendsOpenSegment(last, msg)) last.messages.push(msg);
  else result.push(newInlineSegment(msg, originalIndex));
};
